/* eslint-disable react-hooks/rules-of-hooks */
/**
 *
 * PaymentMethodsV2
 *
 */

import React, { memo, useState, useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import {
  Paper,
  Space,
  Text,
  Conditional,
  translate,
  Cross,
} from '@cashfree-intl/coherent';
import { usePermissionCode } from '@cashfree-intl/auth';

import { MerchantDetailsContext } from 'contexts/MerchantDetailsContext';
import WithReadPermissions from 'components/WithReadPermission';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import {
  objectToURLSearchParams,
  roundOffNumber,
  scrollToTop,
  useQuery,
} from 'utils/common';
import { DEFAULT, PROD, TEST } from 'utils/constants';

import TabularMenu from 'components/TabularMenu';
import Loader from 'components/Loader';
import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';
import { PERM_CODES } from 'constants/permCodes';
import makeSelectPaymentMethodsV2 from './selectors';
import reducer from './reducer';
import saga from './saga';

import { showToast as showToastAction } from '../Toast/actions';

import {
  enableLoaderAction,
  getDocsPerPaymentMethodAction,
  downloadDocumentAction,
  saveUploadDocumentsFormAction,
  enablePaymentMethodAction,
  getPaymentMethodsAction,
  resetFiltersAction,
  updateFiltersAction,
  getPaytmDataAction,
  getPaytmTestConfigAction,
  getPaytmCheckSumHashAction,
  addPaytmProdConfigAction,
  addPaytmTestConfigAction,
  resetPaytmWalletAction,
  getPaypalConfigAction,
  initiatePaypalAction,
  activatePaypalCurrencyAction,
  deactivatePaypalCurrencyAction,
  updateDefaultPaypalCurrencyAction,
  savePaypalDataAction,
  resetPaypalConfigAction,
  updatePaypalConfigAction,
  handleCodHandelingChargesAction,
  handleAccessPinCodesAndAmountAction,
  disableLoaderAction,
} from './actions';

import {
  INTERNATIONAL_WALLET,
  MENU_ITEMS,
  // PAYPAL_CODE,
  // NET_BANKING,
  STANDARD_PRICING_LINK,
  WALLET,
  ORG_TYPES_BLOCKED_FOR_INTERNATIONAL_CARDS,
  INTERNATIONAL_CARDS,
  ACTIVATION_FAILED,
  RESET_PAYTM_MODAL,
  RESET_PAYPAL_MODAL,
  INSTANT_TAT,
  CASH_ON_DELIVERY,
  PAYPAL,
} from './constants';
import {
  getPaypalError,
  filterAttributes,
  formatAttributes,
  prepareUpdatePaypalConfigPayload,
} from './components/utils';

import DashboardFilters from '../DashboardFilters';

import ConnectPaypal from './components/Paypal/ConnectPaypal';
import PaymentMethodsListItem from './components/PaymentMethodsListItem';
import UploadDocuments from './components/UploadDocuments';
import PaypalActivationFailed from './components/Paypal/PaypalActivationFailed';
import ResetPaymentMethod from './components/ResetPaymentMethod';
import ConfirmActivation from './components/ConfirmActivation';
import ActivationFailed from './components/ActivationFailed';
import { getFormattedCodData } from './helpers';

/** ** Add the Amplitude Events - Do not miss this */

export function PaymentMethodsV2(props) {
  useInjectReducer({ key: 'paymentMethodsV2', reducer });
  useInjectSaga({ key: 'paymentMethodsV2', saga });

  const {
    enableLoader,
    disableLoader,
    getPaymentMethods,
    enablePaymentMethod,
    downloadDocument,
    getDocsPerPaymentMethod,
    getPaytmData,
    getPaytmTestConfig,
    getPaytmCheckSumHash,
    addPaytmProdConfig,
    addPaytmTestConfig,
    resetPaytmWallet,
    saveUploadDocumentsForm,
    updateFilters,
    resetFilters,
    getPaypalConfig,
    initiatePaypal,
    activatePaypalCurrency,
    deactivatePaypalCurrency,
    updateDefaultPaypalCurrency,
    savePaypalData,
    resetPaypalConfig,
    showToast,
    updatePaypalConfig,
    paymentMethodsV2: {
      isLoading,
      filters,
      orgType,
      mcc,
      paymentMethodsData,
      paytmProdConfig,
      paytmTestConfig,
      paypalAccount,
      defaultPaypalCurrency,
      activePaypalCurrencies,
      paypalCurrencies,
    },
    handleCodHandelingCharges,
    handleAccessPinCodesAndAmount,
    showFromShopify,
    onCloseShopifyModal,
  } = props;

  const { merchantDetails } = useContext(MerchantDetailsContext);

  const permission =
    showFromShopify || usePermissionCode(PERM_CODES.PG_PAYMENTMETHODS_VIEW);

  const hasPermission = !showFromShopify && permission;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const queryString = {};

  const utmParams = {};

  searchParams.forEach((value, key) => {
    if (/^utm_/.test(key)) utmParams[key] = value;
    else queryString[key] = value;
  });

  const { env, ...paypalCallbackData } = queryString;

  const { pathname, state } = location;
  const isPaypalRedirection =
    state?.paypalRedirection ||
    utmParams?.utm_page?.trim()?.toUpperCase() === PAYPAL ||
    false;

  const isTest = env === TEST;

  const query = useQuery();

  const index = query.get('index') || 0;

  const [activeMenu, setActiveMenu] = useState(MENU_ITEMS[index]);
  // const [active, setActive] = useState(true);
  const [expandItem, setExpandItem] = useState('');
  const [modalName, resetModalName] = useState('');
  const [modalData, resetModalData] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({});
  const [attributes, setAttributes] = useState({});
  const [paypalRedirectionSucess, setPaypalRedirectionSucess] = useState(false);
  const [cashOnDeliveryData, setCashOnDeliveryData] = useState({
    COD_HANDLING_CHARGES: 0,
    COD_ORDER_MINIMUM_AMOUNT: '',
    COD_ORDER_MAXIMUM_AMOUNT: '',
    COD_ORDER_BLOCKED_PIN_CODES: [],
  });

  const paypalData = {
    paypalAccount,
    defaultPaypalCurrency,
    activePaypalCurrencies,
    paypalCurrencies,
  };

  const onItemClick = (e, menu) => {
    setActiveMenu(menu);
    resetFilters();
    // open the first item by default
    const result = paymentMethodsData[menu.key];

    const data = result?.attributes;
    if (data) setExpandItem(data[0].display);
    console.log(data);
    setSelectedPaymentMethod(result);
    setAttributes(formatAttributes(data));

    // check if this is Paytm or Paypal => if yes, then call the api for checking config
    if (menu.key === WALLET) {
      // get Paytm Config
      enableLoader();
      getPaytmData();
    } else if (menu.key === INTERNATIONAL_WALLET) {
      // get Paypal Config
      enableLoader();
      getPaypalConfig();
    } else if (menu.key === CASH_ON_DELIVERY) {
      enableLoader();
      handleCodGetConfig();
    }

    scrollToTop('.payment-methods-paper-content');
    Analytics.track(EVENTS.PAYMENT_METHODS.MENU_CLICK, { name: menu.key });
  };

  const closeModal = () => {
    resetModalName('');
    resetModalData(null);
  };

  const getPageData = () => {
    enableLoader();
    getPaymentMethods();
  };

  const paypalCallbackFn = () => {
    getPageData();
    onItemClick({}, { key: INTERNATIONAL_WALLET, name: INTERNATIONAL_WALLET });

    // use this fn objectToURLSearchParams for utmParams
    // also i think we can hardcode env to always be set

    navigate(
      `${pathname}${`?env=${env || PROD}`}${
        Object.keys(utmParams).length
          ? `&${objectToURLSearchParams(utmParams)}`
          : ''
      }`,
      {
        state: { paypalRedirection: true },
      },
    );
  };

  const savePaypalDataFn = data => {
    // call the api for saving the paypal data
    enableLoader();
    savePaypalData({
      data,
      successCb: response => {
        if (!response.active) {
          const errorData = getPaypalError(response);
          // console.log('error data - ', errorData);
          resetModalData(errorData);
          resetModalName('PAYPAL_ACTIVATION_FAILED');
        }

        paypalCallbackFn();
      },
      errorCb: paypalCallbackFn,
    });
  };

  const handleCodSaveConfig = (pinCodeData, codChargesData) => {
    enableLoader();
    handleCodHandelingCharges({
      type: 'POST',
      reqData: codChargesData,
      successCb: () => {
        showToast({
          type: 'SUCCESS',
          message: `${translate('cod_data_added_successfully')}`,
        });
        disableLoader();
      },
      errorCb: err => {
        showToast(err);
        disableLoader();
      },
    });
    handleAccessPinCodesAndAmount({
      type: 'POST',
      reqData: pinCodeData,
      successCb: () => {
        showToast({
          type: 'SUCCESS',
          message: `${translate('cod_data_added_successfully')}`,
        });
        disableLoader();
      },
      errorCb: err => {
        showToast(err);
        disableLoader();
      },
    });
  };

  const handleCodGetConfig = () => {
    handleCodHandelingCharges({
      type: 'GET',
      reqData: {
        configName: 'COD_HANDLING_CHARGES',
        paymentCode: 12002,
      },
      successCb: response => {
        let newObj = {};
        if (Object.keys(response).length !== 0) {
          newObj = {
            [response?.configName]: response.configValue,
          };
        }
        setCashOnDeliveryData(p => ({ ...p, ...newObj }));
      },
      errorCb: chargesObj => {
        setCashOnDeliveryData(p => ({ ...p, ...chargesObj }));
      },
    });
    handleAccessPinCodesAndAmount({
      type: 'GET',
      successCb: response => {
        setCashOnDeliveryData(p => ({
          ...p,
          ...getFormattedCodData(response),
        }));
      },
      errorCb: pinCodeObj => {
        setCashOnDeliveryData(p => ({
          ...p,
          ...pinCodeObj,
        }));
      },
    });
  };

  const handleCODSubmit = ({ COD_HANDLING_CHARGES, ...rest }) => {
    const validHandleingChanges = {
      configValue: !COD_HANDLING_CHARGES ? 10 : COD_HANDLING_CHARGES,
      status: !COD_HANDLING_CHARGES ? 'INACTIVE' : 'ACTIVE',
    };
    handleCodSaveConfig(rest, {
      configName: 'COD_HANDLING_CHARGES',
      paymentCode: 12002,
      ...validHandleingChanges,
    });
  };

  useEffect(() => {
    // if paypal callback data exists
    if (paypalCallbackData && Object.keys(paypalCallbackData).length > 0) {
      savePaypalDataFn(paypalCallbackData);
    } else {
      if (isPaypalRedirection) {
        onItemClick(
          {},
          { key: INTERNATIONAL_WALLET, name: INTERNATIONAL_WALLET },
        );
      }
      getPageData();
    }
    handleCodGetConfig();
  }, [env]);

  useEffect(() => {
    let result;
    if (
      !selectedPaymentMethod ||
      Object.keys(selectedPaymentMethod).length === 0
    ) {
      result =
        paymentMethodsData[
          isPaypalRedirection ? INTERNATIONAL_WALLET : MENU_ITEMS[0].key
        ];
      // clearing the location state after it has served it's purpose
      if (isPaypalRedirection && Object.keys(paymentMethodsData).length) {
        setPaypalRedirectionSucess(true);
        navigate(
          `${pathname}${`?env=${env || PROD}`}${
            Object.keys(utmParams).length
              ? `&${objectToURLSearchParams(utmParams)}`
              : ''
          }`,
        );
      }
    } else if (selectedPaymentMethod.uniqueIdentifier) {
      result = paymentMethodsData[selectedPaymentMethod.uniqueIdentifier];
    }

    if (result) {
      const data = result?.attributes;
      setExpandItem(expandItem || data[0].display);
      setSelectedPaymentMethod(result);
      setAttributes(formatAttributes(data));
    }
  }, [paymentMethodsData]);

  const enablePaymentMethodsSubmit = (modeDetails, canBeActivatedInstantly) => {
    const {
      paymentMethods,
      mode: { salesForceId },
    } = modeDetails;

    let isInstantActive = false;

    if (canBeActivatedInstantly) {
      isInstantActive = !!(
        modalData && modalData.tat?.trim().toUpperCase() === INSTANT_TAT
      );
    }

    resetModalName('');
    enableLoader();
    enablePaymentMethod({
      paymentMethods,
      salesForceIds: salesForceId,
      isInstantActive,
      successCb: () => {
        Analytics.track(EVENTS.PAYMENT_METHODS.MODE_ENABLE, {
          paymentMethods,
          status: 'SUCCESS',
        });

        showToast({
          message: isInstantActive
            ? `${modalData.display} payment mode has been successfully enabled`
            : `Request to enable ${
                modalData.display
              } has been successfully submitted`, // eslint-disable-line
          type: 'SUCCESS',
        });
        resetModalData(null);
      },
      // handle the Phonepe error scenario here in the errorCb
      errorCb: err => {
        if (isInstantActive) resetModalName(ACTIVATION_FAILED);
        Analytics.track(EVENTS.PAYMENT_METHODS.MODE_ENABLE, {
          paymentMethods,
          status: 'ERROR',
          message: err.message,
        });
      },
    });
  };

  const handleDocumentUpload = formData => {
    const { modeDetails, fieldsToBeUploaded, ...data } = formData;

    saveUploadDocumentsForm({
      data,
      successCb: () => {
        // show the request submitted successfully modal
        resetModalName('');
        // call the enable payment methods submit here
        enablePaymentMethodsSubmit(modeDetails, false);
        Analytics.track(EVENTS.PAYMENT_METHODS.UPLOAD_DOCUMENTS_SUBMIT, {
          ...fieldsToBeUploaded,
          data: modeDetails,
          status: 'SUCCESS',
        });
      },
      errorCb: err => {
        Analytics.track(EVENTS.PAYMENT_METHODS.UPLOAD_DOCUMENTS_SUBMIT, {
          ...fieldsToBeUploaded,
          data: modeDetails,
          status: 'ERROR',
          message: err.message,
        });
        // console.log(err);
      },
    });
  };

  const connectPaypalAccount = url => {
    window.open(url, '_blank');
  };

  const connectPaypal = () => {
    enableLoader();
    closeModal();
    initiatePaypal({
      cb: connectPaypalAccount,
    });
    Analytics.track(EVENTS.PAYMENT_METHODS.CONNECT_PAYPAL);
  };

  const onPaypalToggleClick = isPaypalActive => {
    // open the modal for connect account
    if (isPaypalActive) {
      resetModalName(RESET_PAYPAL_MODAL);
      resetModalData(null);
    } else {
      resetModalName('CONNECT_PAYPAL_ACCOUNT');
      resetModalData(null);
    }
  };

  const onToggleClick = ({ mode, combinedModes = [] }) => {
    const paymentMethods = [...mode.paymentCode];
    combinedModes.forEach(obj => {
      paymentMethods.push(...obj.paymentCode);
    });

    Analytics.track(EVENTS.PAYMENT_METHODS.TOGGLE_CLICK, { paymentMode: mode });

    // enable loader
    enableLoader();
    // // get docs needed to enable this
    getDocsPerPaymentMethod({
      data: {
        paymentMethods,
      },
      cb: response => {
        // see if documents are needed else directly show the confirmation modal
        if (response?.length) {
          resetModalData({
            mode,
            paymentMethods,
            key: activeMenu.key,
            // display: activeMenu.key === NET_BANKING ? activeMenu.name : display,
            display: mode.display,
            combinedModes,
            documents: response,
          });
          resetModalName('UPLOAD_DOCUMENTS');
        } else {
          resetModalData({
            mode,
            tat: mode.tat,
            tdr: mode.tdr,
            paymentMethods,
            combinedModes,
            display: mode.display,
          });
          resetModalName('CONFIRM_MODAL');
        }
      },
    });
  };

  const resetPaymentMethod = type => {
    enableLoader();
    if (type === RESET_PAYPAL_MODAL) {
      resetPaypalConfig({
        successCb: () => getPaypalConfig(),
        errorCb: () => {},
      });
      Analytics.track(EVENTS.PAYMENT_METHODS.RESET_PAYPAL);
    } else {
      resetPaytmWallet({
        successCb: () => getPaytmData(),
        errorCb: () => {},
      });
      Analytics.track(EVENTS.PAYMENT_METHODS.RESET_PAYTM);
    }
    resetModalName('');
  };

  const { disablePaymentGroup, filteredAttributes, isAnyModeActive } = useMemo(
    () =>
      filterAttributes({
        data: attributes,
        filters: filters.appliedFilters,
        paytmTestConfig,
        paypalAccount,
      }),
    [attributes, filters.appliedFilters, paytmTestConfig, paypalAccount],
  );

  const disableInternationalCards =
    activeMenu.key === INTERNATIONAL_CARDS &&
    !isAnyModeActive &&
    ORG_TYPES_BLOCKED_FOR_INTERNATIONAL_CARDS.includes(
      orgType?.trim().toUpperCase(),
    );

  const openStandardPricingLink = () => {
    window.open(STANDARD_PRICING_LINK, '_blank');
    Analytics.track(EVENTS.PAYMENT_METHODS.STANDARD_PRICING);
  };

  const onUpdatePaypalConfig = ({ markup, enableForexMarkup }) => {
    enableLoader();
    const { paypalAccount: paypalAccountData } = paypalData;
    updatePaypalConfig({
      data: prepareUpdatePaypalConfigPayload({
        ...paypalAccountData,
        forexMarkupEnabled: enableForexMarkup,
        conFee: +roundOffNumber(markup),
      }),
      cb: getPaypalConfig,
    });
  };

  const renderPaymentMethodListItem = () =>
    Object.keys(filteredAttributes).map((key, idx) => (
      <div
        className={idx === 0 ? 'payment-methods-list-first-item' : ''}
        key={key}
      >
        <PaymentMethodsListItem
          key={key}
          showListing={Object.keys(filteredAttributes).length > 1}
          data={filteredAttributes[key]}
          expandItem={expandItem === key}
          setExpandItem={setExpandItem}
          onToggleClick={onToggleClick}
          mcc={mcc}
          disableInternationalCards={disableInternationalCards}
          disablePaymentGroup={disablePaymentGroup}
          paytmData={{
            data: { paytmTestConfig, paytmProdConfig },
            getPaytmTestConfig,
            getPageData,
            getPaytmCheckSumHash,
            addPaytmProdConfig,
            addPaytmTestConfig,
            showToast,
          }}
          paypalData={{
            data: paypalData,
            onToggleClick: onPaypalToggleClick,
            activatePaypalCurrency,
            deactivatePaypalCurrency,
            updateDefaultPaypalCurrency,
            updatePaypalConfig: onUpdatePaypalConfig,
            paypalRedirectionSucess,
          }}
          cashOnDeliveryData={cashOnDeliveryData}
          onCodSubmit={handleCODSubmit}
        />
      </div>
    ));

  if (!showFromShopify && !hasPermission) {
    return (
      <WithReadPermissions
        description="to access Payment methods"
        merchantEmail={merchantDetails.merchantEmail}
      />
    );
  }

  return (
    <div>
      <Loader active={isLoading} />
      <Conditional if={showFromShopify}>
        <Space gap={2} alignItems="flex-start" justifyContent="space-between">
          <Text variant="h28" className="mb-1">
            Payment Methods
          </Text>
          <Cross onClick={onCloseShopifyModal} />
        </Space>
      </Conditional>
      <Conditional if={!showFromShopify}>
        <Text variant="h28" className="mb-1">
          Payment Methods
        </Text>
      </Conditional>
      <Conditional if={isTest}>
        <Text variant="b14" color="bodyLight" className="mb-4">
          Payment methods that are enabled for your test account are shown below
          with status as Active.
          <br /> Contact your account manager or write to us at{' '}
          <a target="_blank" href="mailto:care@cashfree.com">
            care@cashfree.com
          </a>{' '}
          if you wish to enable any other payment methods in test environment.
          {/* eslint-disable-next-line */}
          Click <a onClick={openStandardPricingLink}>here</a> to learn more
          about the payment methods Cashfree supports.
        </Text>
      </Conditional>
      <Conditional if={!isTest}>
        <Text variant="b14" color="bodyLight" className="mb-4">
          Payment methods offered by Cashfree, along with the status, are shown
          here. You can enable more payment methods by requesting for them from
          merchant dashboard.
          <br /> Contact your Account Manager or write to us at{' '}
          <a target="_blank" href="mailto:care@cashfree.com">
            care@cashfree.com
          </a>{' '}
          if you face issues in enabling or wish to disable any payment method.
          Click{' '}
          <a href={STANDARD_PRICING_LINK} target="_blank">
            here
          </a>{' '}
          to learn more about the payment methods Cashfree supports.
        </Text>
      </Conditional>
      <Paper className="payment-methods-paper w-100 p-3">
        <div className="payment-methods-paper-wrapper">
          <TabularMenu
            activeMenu={activeMenu.key}
            onItemClick={onItemClick}
            menuItems={MENU_ITEMS}
          />
          <div
            className={`payment-methods-paper-content p-3 ${
              activeMenu.key === MENU_ITEMS[0].key ? 'active-first-item' : ''
            } ${disablePaymentGroup ? 'disabled-payment-group' : ''}`}
          >
            <Space justifyContent="space-between" gap={3}>
              <Text variant="h20" className="payment-method-title">
                {activeMenu.name}
              </Text>

              <Conditional if={disableInternationalCards}>
                <Text color="danger" strong className="text-center font-normal">
                  As per regulations, individual merchants cannot accept
                  payments via international cards.
                </Text>
              </Conditional>
              <Conditional if={activeMenu.key !== CASH_ON_DELIVERY}>
                <DashboardFilters
                  menuSelected="PAYMENT_METHODS"
                  filters={filters}
                  filtersForDisplay={[]}
                  activeNavigation={DEFAULT}
                  resetFilterFn={resetFilters}
                  updateFilterFn={updateFilters}
                />
              </Conditional>
            </Space>
            <div
              className={`${activeMenu.key === CASH_ON_DELIVERY ? 'mt-4' : ''}`}
            >
              <Conditional if={selectedPaymentMethod}>
                {renderPaymentMethodListItem()}
              </Conditional>
            </div>
            <Conditional if={disablePaymentGroup}>
              <Space
                alignItems="center"
                justifyContent="center"
                className="disabled-mode-paper-wrapper"
              >
                <Paper className="p-2 disabled-mode-paper">
                  This payment method is disabled for your account. Contact your
                  account manager or write to us at{' '}
                  <span className="w-700">care@cashfree.com</span> to enable it.
                  Please note that payment modes will resume their previous
                  states after enablement.
                </Paper>
              </Space>
            </Conditional>
          </div>
        </div>
      </Paper>
      {modalName === 'UPLOAD_DOCUMENTS' && (
        <UploadDocuments
          closeModal={closeModal}
          modeDetails={modalData}
          handleFormSubmit={handleDocumentUpload}
          downloadDocument={downloadDocument}
        />
      )}
      {modalName === ACTIVATION_FAILED && modalData?.display && (
        <ActivationFailed closeModal={closeModal} modalData={modalData} />
      )}
      <Conditional if={modalName === 'CONNECT_PAYPAL_ACCOUNT'}>
        <ConnectPaypal closeModal={closeModal} connectPaypal={connectPaypal} />
      </Conditional>
      <Conditional if={modalName === 'CONFIRM_MODAL'}>
        <ConfirmActivation
          modalData={modalData}
          closeModal={closeModal}
          enablePaymentMethodsSubmit={enablePaymentMethodsSubmit}
        />
      </Conditional>
      <Conditional
        if={modalName === RESET_PAYPAL_MODAL || modalName === RESET_PAYTM_MODAL}
      >
        <ResetPaymentMethod
          closeModal={closeModal}
          modalName={modalName}
          resetPaymentMethod={resetPaymentMethod}
        />
      </Conditional>
      <Conditional if={modalName === 'PAYPAL_ACTIVATION_FAILED'}>
        <PaypalActivationFailed closeModal={closeModal} data={modalData} />
      </Conditional>
    </div>
  );
}

PaymentMethodsV2.propTypes = {
  enableLoader: PropTypes.func.isRequired,
  paymentMethodsV2: PropTypes.object.isRequired,
  getDocsPerPaymentMethod: PropTypes.func.isRequired,
  saveUploadDocumentsForm: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
  enablePaymentMethod: PropTypes.func.isRequired,
  getPaymentMethods: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  getPaytmData: PropTypes.func.isRequired,
  getPaytmTestConfig: PropTypes.func.isRequired,
  getPaytmCheckSumHash: PropTypes.func.isRequired,
  addPaytmProdConfig: PropTypes.func.isRequired,
  addPaytmTestConfig: PropTypes.func.isRequired,
  resetPaytmWallet: PropTypes.func.isRequired,
  getPaypalConfig: PropTypes.func.isRequired,
  resetPaypalConfig: PropTypes.func.isRequired,
  initiatePaypal: PropTypes.func.isRequired,
  savePaypalData: PropTypes.func.isRequired,
  activatePaypalCurrency: PropTypes.func.isRequired,
  deactivatePaypalCurrency: PropTypes.func.isRequired,
  updateDefaultPaypalCurrency: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  orgType: PropTypes.string.isRequired,
  handleCodHandelingCharges: PropTypes.func.isRequired,
  handleAccessPinCodesAndAmount: PropTypes.func.isRequired,
  updatePaypalConfig: PropTypes.func.isRequired,
  disableLoader: PropTypes.func.isRequired,
  showFromShopify: PropTypes.bool,
  onCloseShopifyModal: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  paymentMethodsV2: makeSelectPaymentMethodsV2(),
});

function mapDispatchToProps(dispatch) {
  return {
    enableLoader: () => dispatch(enableLoaderAction()),
    disableLoader: () => dispatch(disableLoaderAction()),
    getPaymentMethods: () => dispatch(getPaymentMethodsAction()),
    enablePaymentMethod: data => dispatch(enablePaymentMethodAction(data)),
    getDocsPerPaymentMethod: data =>
      dispatch(getDocsPerPaymentMethodAction(data)),
    saveUploadDocumentsForm: data =>
      dispatch(saveUploadDocumentsFormAction(data)),
    downloadDocument: data => dispatch(downloadDocumentAction(data)),
    resetFilters: () => dispatch(resetFiltersAction()),
    updateFilters: data => dispatch(updateFiltersAction(data)),
    getPaytmData: () => dispatch(getPaytmDataAction()),
    getPaytmTestConfig: () => dispatch(getPaytmTestConfigAction()),
    getPaytmCheckSumHash: data => dispatch(getPaytmCheckSumHashAction(data)),
    addPaytmProdConfig: data => dispatch(addPaytmProdConfigAction(data)),
    addPaytmTestConfig: data => dispatch(addPaytmTestConfigAction(data)),
    resetPaytmWallet: data => dispatch(resetPaytmWalletAction(data)),
    getPaypalConfig: () => dispatch(getPaypalConfigAction()),
    initiatePaypal: data => dispatch(initiatePaypalAction(data)),
    activatePaypalCurrency: data =>
      dispatch(activatePaypalCurrencyAction(data)),
    deactivatePaypalCurrency: data =>
      dispatch(deactivatePaypalCurrencyAction(data)),
    updateDefaultPaypalCurrency: data =>
      dispatch(updateDefaultPaypalCurrencyAction(data)),
    savePaypalData: data => dispatch(savePaypalDataAction(data)),
    resetPaypalConfig: data => dispatch(resetPaypalConfigAction(data)),
    showToast: data => dispatch(showToastAction(data)),
    handleCodHandelingCharges: data =>
      dispatch(handleCodHandelingChargesAction(data)),
    handleAccessPinCodesAndAmount: data =>
      dispatch(handleAccessPinCodesAndAmountAction(data)),
    updatePaypalConfig: data => dispatch(updatePaypalConfigAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(PaymentMethodsV2);
